import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { getProject } from '../../../../Requests/ProjectRequests';
import { getProjectTasks, getTaskStatuses, getTaskCategories } from '../../../../Requests/TaskRequests';
import { useSidebar } from '../../../../Components/ContextProviders/SidebarContextProvider';
import { getSidebarPadding } from '../../../../utils/sidebar';
import { getTeamUuid } from '../../../../utils/cookies';
import { ChevronLeftIcon } from '@heroicons/react/20/solid';
import { SwatchIcon } from '@heroicons/react/24/outline';
import TaskKanbanBoard from '../../../../Components/Tasks/TaskKanbanBoard';
import TaskForm from '../../../../Components/Tasks/TaskForm';
import CategoryManager from '../../../../Components/Tasks/CategoryManager';
import UpgradedLoader from '../../../../Components/UpgradedLoader';

function TaskKanban() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { project_uuid } = useParams();
    const { isCollapsed } = useSidebar();
    const [project, setProject] = useState(null);
    const [tasks, setTasks] = useState([]);
    const [statuses, setStatuses] = useState([]);
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showTaskForm, setShowTaskForm] = useState(false);
    const [showCategoryManager, setShowCategoryManager] = useState(false);

    useEffect(() => {
        if (project_uuid) {
            // Load project, tasks, statuses, and categories in parallel
            Promise.all([
                getProject(project_uuid),
                getProjectTasks(project_uuid),
                getTaskStatuses(getTeamUuid()),
                getTaskCategories(getTeamUuid())
            ]).then(([projectResponse, tasksResponse, statusesResponse, categoriesResponse]) => {
                setProject(projectResponse.data);

                // Handle different response structures for tasks
                const tasksData = tasksResponse.data?.tasks || tasksResponse.data?.data?.tasks || tasksResponse.data || [];
                setTasks(Array.isArray(tasksData) ? tasksData : []);
                
                // Set statuses and categories
                setStatuses(statusesResponse.data || []);
                setCategories(categoriesResponse.data || []);
            }).catch((error) => {
                console.error('Error loading project data:', error);
                navigate('/projects');
            }).finally(() => {
                setLoading(false);
            });
        }
    }, [project_uuid, navigate]);

    const handleTaskCreate = (newTask) => {
        setTasks([...tasks, newTask]);
        setShowTaskForm(false);
    };

    const handleTaskDelete = (taskToDelete) => {
        // This won't be called since task={null} for new task creation
        // But added for consistency with the component interface
        const updatedTasks = tasks.filter(t => t.id !== taskToDelete.id);
        setTasks(updatedTasks);
        setShowTaskForm(false);
    };

    if (loading) {
        return (
            <div className={`${getSidebarPadding(isCollapsed)} p-6`}>
                <div className="flex items-center justify-center py-12">
                    <UpgradedLoader className="animate-spin" w={8} h={8} bg="bg-black" />
                </div>
            </div>
        );
    }

    if (!project) {
        return (
            <div className={`${getSidebarPadding(isCollapsed)} p-6`}>
                <div className="text-center py-12">
                    <p className="text-gray-500">{t('Projects.NotFound')}</p>
                </div>
            </div>
        );
    }

    return (
        <div className={`${getSidebarPadding(isCollapsed)} transition-all duration-300 pt-4 w-full h-screen flex flex-col justify-start`}>
            <div className='flex flex-col w-full justify-start items-center px-4 h-full'>
                {/* Navigation Breadcrumb */}
                <div className="mb-6 w-full max-w-7xl">
                    <nav className="flex items-center space-x-2 text-sm text-gray-500">
                        <button
                            onClick={() => navigate('/projects')}
                            className="flex items-center hover:text-gray-700"
                        >
                            <ChevronLeftIcon className="h-4 w-4 mr-1" />
                            {t('ProjectOverview.AllProjects')}
                        </button>
                        <span>›</span>
                        <button
                            onClick={() => navigate(`/project/${project_uuid}`)}
                            className="hover:text-gray-700"
                        >
                            {project.name}
                        </button>
                        <span>›</span>
                        <span className="text-gray-900 font-medium">{t('Tasks.KanbanBoard')}</span>
                    </nav>
                </div>

                {/* Task Board Header */}
                <div className="bg-white max-w-7xl rounded-2xl w-full shadow-xl mb-6">
                    <div className="px-4 py-5 sm:px-6">
                        <div className="md:flex md:items-center md:justify-between">
                            <div className="min-w-0 flex-1">
                                <h2 className="text-2xl/7 font-bold text-gray-900 sm:truncate sm:text-3xl sm:tracking-tight">
                                    {t('Tasks.KanbanBoard')}
                                </h2>
                                <p className="mt-1 text-sm text-gray-500 sm:truncate">
                                    {t('Tasks.KanbanSubtitle')}
                                </p>
                            </div>
                            <div className="mt-4 flex shrink-0 md:ml-4 md:mt-0 space-x-2">
                                <button
                                    type="button"
                                    onClick={() => setShowTaskForm(true)}
                                    className="inline-flex items-center rounded-md bg-iavisus-100 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-iavisus-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-iavisus-100"
                                >
                                    <svg className="-ml-0.5 mr-1.5 h-5 w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                                        <path d="M10.75 4.75a.75.75 0 00-1.5 0v4.5h-4.5a.75.75 0 000 1.5h4.5v4.5a.75.75 0 001.5 0v-4.5h4.5a.75.75 0 000-1.5h-4.5v-4.5z" />
                                    </svg>
                                    {t('Tasks.AddTask')}
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setShowCategoryManager(true)}
                                    className="inline-flex items-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                                >
                                    <SwatchIcon className="-ml-0.5 mr-1.5 h-5 w-5" />
                                    {t('Tasks.ManageCategories')}
                                </button>
                                <button
                                    type="button"
                                    onClick={() => navigate(`/project/${project_uuid}`)}
                                    className="inline-flex items-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                                >
                                    {t('Common.Back')}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Kanban Board */}

                <div className="flex w-full max-w-7xl">
                    <TaskKanbanBoard
                        onClose={() => navigate(`/project/${project_uuid}`)}
                        tasks={tasks}
                        onTasksUpdate={setTasks}
                        statuses={statuses}
                        categories={categories}
                        isFullPage={true}
                    />
                </div>
            </div>

            {/* Task Form Drawer */}
            <TaskForm
                open={showTaskForm}
                task={null}
                project={project}
                statuses={statuses}
                categories={categories}
                onSave={handleTaskCreate}
                onDelete={handleTaskDelete}
                onCancel={() => setShowTaskForm(false)}
            />

            {/* Category Manager Modal */}
            <CategoryManager
                open={showCategoryManager}
                onClose={() => setShowCategoryManager(false)}
                categories={categories}
                onCategoriesUpdate={setCategories}
            />
        </div>
    );
}

export default TaskKanban;
